/**
 * The account: the OAuth popup, applying a saved login, logging out, and
 * starting the sync of settings to the account once the account's copy has
 * been read. Legacy kept all of this in Games.js.
 *
 * The popup window opened on the login origin ends up posting a message back
 * to this window like so:
 *
 *   <html>
 *     <head>
 *       <script type="text/javascript">
 *         function closePopup(){
 *           window.opener.postMessage({
 *               nonce:     "…",                          // randomly generated by client, for CSRF prevention
 *               tokens:    { game: "…", settings: "…" }, // used by client to authenticate with game servers and the settings service
 *               provider:   …,                           // number indicating which identity provider  }- these are for client
 *               loginname: "…",                          // account name or email                      }  display only
 *              }, "https://…"); // expected to match origin of window.opener e.g. https://airmash.rocks
 *           window.close();
 *         }
 *       </script>
 *     </head>
 *     <body onload="closePopup()"></body>
 *   </html>
 *
 * On the original airma.sh, the login window could call window.opener.loginSuccess()
 * directly as they had the same origin. The login flow is shared by several
 * domains now, so the popup uses Window.postMessage() for cross-origin
 * communication and this module listens for it.
 */
import { removeKey } from '../services/LocalStorage';
import { IDENTITY_PROVIDERS, uiState } from '../ui/UIState';
import type { Settings } from './Config';
import { LOGIN_ORIGIN } from './Constants';
import { applySettingsToGame } from './Games';
import { ajaxGet } from './Http';
import {
  auth,
  loadAuth,
  removeSetting,
  setAuth,
  setSettings,
  settings,
  startRemoteSettingsSync,
  toRemoteSettings,
} from './Settings';
import { randomID as toolsRandomID } from './Tools';
import type { AuthTokens, IdentityProviderId, LoginMessageData } from './types/Auth';
import { closeAllPanels as uiCloseAllPanels, setGameNotification as uiSetGameNotification } from './UI';

/** The login whose popup is open: the provider, and the nonce its reply must echo. Null between logins. */
let pendingLogin: { provider: IdentityProviderId; nonce: string } | null = null;

/** Runs once the account's settings have been applied; the boot hands it GameAPI.syncState. */
let onSettingsApplied: () => void = () => {};

/**
 * Applies a saved login, if any, and listens for the popup's reply.
 * `settingsApplied` runs after the account's settings replace this device's,
 * at boot or after a sign-in, so what mirrors them for the menu is refreshed;
 * it had been refreshed only when the menu drawer next opened.
 */
export function setup(settingsApplied: () => void): void {
  onSettingsApplied = settingsApplied;

  // A saved login applies at once; a guest is the UI's default state
  if (loadAuth()) {
    playerAuth(false);
  }

  // One listener for the life of the page. Other windows post here too
  // (extensions talk to their content scripts this way), so a login is only
  // ever completed by the reply the open popup was sent for; a one-shot
  // listener was consumed by whichever message came first.
  window.addEventListener('message', receiveLoginMessage);
}

/** True for one of the login service's provider numbers. */
function isIdentityProviderId(value: unknown): value is IdentityProviderId {
  return typeof value === 'number' && Object.hasOwn(IDENTITY_PROVIDERS, value);
}

/** True for a token pair with both tokens present. */
function isLoginTokens(value: unknown): value is Required<AuthTokens> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'game' in value &&
    typeof value.game === 'string' &&
    'settings' in value &&
    typeof value.settings === 'string'
  );
}

/** True when the popup's reply has the shape the login service posts. */
function isLoginMessageData(value: unknown): value is LoginMessageData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'nonce' in value &&
    typeof value.nonce === 'string' &&
    'provider' in value &&
    isIdentityProviderId(value.provider) &&
    'loginname' in value &&
    typeof value.loginname === 'string' &&
    'tokens' in value &&
    isLoginTokens(value.tokens)
  );
}

/**
 * Completes the pending login from the popup's reply. Only that reply counts:
 * the login origin, the shape above, and the pending provider and nonce.
 * Anything else is ignored and leaves the pending login in place.
 */
function receiveLoginMessage(msg: MessageEvent<unknown>): void {
  if (pendingLogin === null || msg.origin !== LOGIN_ORIGIN) {
    return;
  }

  const reply = msg.data;
  if (!isLoginMessageData(reply)) {
    console.warn('Login message has an unexpected shape');
    return;
  }

  if (reply.provider !== pendingLogin.provider) {
    console.warn(`Identity provider does not match: ${reply.provider}`);
    return;
  }

  if (reply.nonce !== pendingLogin.nonce) {
    console.warn(`Nonce does not match: ${reply.nonce}`);
    return;
  }
  pendingLogin = null;

  setAuth({ tokens: reply.tokens, identityprovider: reply.provider, loginname: reply.loginname });
  playerAuth(true);
}

/** Opens the popup for the given identity provider; its reply completes the sign-in. */
export function popupLogin(identityProvider: IdentityProviderId): void {
  const nonce = toolsRandomID(32);
  pendingLogin = { provider: identityProvider, nonce };
  const url = `${LOGIN_ORIGIN}/login?provider=${identityProvider}&nonce=${nonce}&origin=${window.origin}`;

  // The original's sizes: Reddit (4) gets a wide window, Twitch (5) a tall one
  openLoginWindow(url, 'Login', identityProvider === 4 ? 900 : 500, identityProvider === 5 ? 800 : 600);
}

/** Opens a popup window of the given size, centred on this window. */
function openLoginWindow(url: string, title: string, width: number, height: number) {
  const left = window.innerWidth / 2 - width / 2 + window.screenX;
  const top = window.innerHeight / 2 - height / 2 + window.screenY;
  window.open(url, title, `width=${width}, height=${height}, top=${top}, left=${left}`);
}

/**
 * Fetches the account's settings and switches the UI to the account once they
 * arrive. A failed fetch leaves the guest UI in place, as legacy's null
 * callback did; the login stays saved, so the next load tries again. After a
 * popup sign-in the player just acted, so that case closes the panel and says
 * so instead of staying silent.
 */
function playerAuth(fromPopup: boolean): void {
  const settingsToken = auth.tokens?.settings;
  ajaxGet<Settings>('/settings', settingsToken)
    .catch(() => null)
    .then((remoteSettings) => {
      if (remoteSettings == null) {
        if (fromPopup) {
          uiCloseAllPanels();
          uiSetGameNotification({
            type: 'alert',
            title: '',
            showReconnect: false,
            timeoutMs: 10000,
            message: 'Signed in, but your account settings could not be loaded\nRefresh the page to try again',
          });
        }
        return;
      }

      // Update uiState for Lit UI
      const provider = auth.identityprovider;
      uiState.isLoggedIn = true;
      uiState.loginName = (auth.loginname || '').substring(0, 30);
      uiState.identityProvider = provider != null ? IDENTITY_PROVIDERS[provider] : null;

      // The account's copy wins setting by setting, as in legacy, where every
      // setting was top-level. The game group holds many settings, so it merges
      // field by field, keeping any this device set that the account lacks.
      // Everything else, keybinds included, replaces this device's copy whole,
      // except the id and display group, which never leave this device.
      const accountSettings = toRemoteSettings(remoteSettings);
      if (accountSettings.game != null) {
        accountSettings.game = { ...settings.game, ...accountSettings.game };
      }
      setSettings(accountSettings);
      applySettingsToGame();
      onSettingsApplied();

      // The account's copy has been read, so changes can be written back.
      // Legacy uploaded on any token, so a device whose fetch had failed
      // overwrote the account with its own copy a second later.
      startRemoteSettingsSync();
    });
}

/**
 * Logs out: clears the auth, the name and flag the account carried, and the
 * lobby's own copy of the name, so the next person on this machine starts
 * blank as legacy's did, then reloads.
 */
export function logout(): void {
  setAuth({});
  removeSetting('flag');
  removeSetting('name');
  removeKey('playerName');
  window.location.reload();
}
